import { useEffect, useState } from "react";

// LocalStorage-backed "recently viewed" product list. Stores a small
// snapshot of each product (enough to render a card) so the Home page
// and other consumers do not need a fresh API round-trip to show them.
// Cap at 8 entries; MRU order (most recent first). Silently no-ops if
// localStorage is unavailable (Safari private mode, etc.).

const KEY = "wa_recently_viewed";
const MAX = 8;

function readStore() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeStore(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
  } catch {
    // ignore quota / disabled storage
  }
}

// Read-only accessor (Home page uses this).
export function useRecentlyViewed() {
  const [list, setList] = useState(readStore);
  // Re-read once on mount in case another tab wrote to the same key.
  useEffect(() => {
    setList(readStore());
    const onStorage = (e) => { if (e.key === KEY) setList(readStore()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return list;
}

// Add the given product to the top of the list. Call on ProductDetail
// mount. `product` may be the full product object; only the fields we
// need for rendering are stored.
export function recordViewed(product) {
  if (!product?.slug) return;
  const snapshot = {
    id: product.id || product.slug,
    slug: product.slug,
    name: product.name,
    category: product.category,
    price: product.price,
    image: product.images?.[0] || null,
    at: Date.now(),
  };
  const current = readStore().filter((p) => p.slug !== snapshot.slug);
  writeStore([snapshot, ...current]);
}
